import React from 'react';
import { StarFilled } from '@wix/wix-ui-icons-common';
import s from './solution-info.scss';
import { composer } from '../../../../component-decorators/composer';
import { config } from '../../../../config';
import { HeaderContext } from '../solution-page-header/header-context';
import {
  getBundleAppSavedPrice,
  isBundleAppAndInstalled,
  isBundleAppAndNotInstalled,
} from '../../../../common/utils';
import {
  TeaserPreviewWrapper,
  DevelopedByWrapper,
} from '../../preview-elements';
import { AddToSite } from './add-to-site';
import { VisibilityDetector } from '@wix/app-market-components';
import type { GUID } from '@wix/bi-logger-market/v2/types';
import { SolutionBadge } from '@wix/app-market-collection-widget';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { Heading, Text } from '@wix/design-system';
import type { TFunction } from 'i18next';
import { AppMarketLink } from '../../../app-market-link';
import { Origin, Path } from '../../../../enums/marketplace-enums';
import type { Pricing, WSTag } from '@wix/ambassador-marketplace/types';
import { isAppPendingConfiguration } from '../../../../common/is-app-pending-configuration';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { IncompleteSetupReason } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import type { IAppDependenciesProps } from '../solution-page';

export const dtoToDetailsProps = ({
  company,
  baseInfo,
  isNotAvailableToInstall,
}: any): IDetailsProps => {
  const {
    id,
    name,
    teaser,
    icon,
    numberOfReviews,
    reviewStars,
    openConsentIn,
    Tags,
    showRegularAppPage,
  } = baseInfo;
  return {
    companySlug: company?.companyBase?.slug,
    companyId: company?.companyBase?.id,
    id,
    name,
    icon,
    teaser,
    numberOfReviews,
    reviewStars,
    showRegularAppPage,
    Tags,
    pricing: baseInfo.pricing,
    developerName: baseInfo.company ? baseInfo.company.name : 'Dev Name',
    openConsentIn,
    isRequiredWixPremium:
      baseInfo.pricing && baseInfo.pricing.isRequiredWixPremium,
    biData: dtoBaseToDeatilsBiData(baseInfo),
    isNotAvailableToInstall,
  };
};

export interface IDetailsProps extends Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  t?: TFunction;
  companySlug: string;
  companyId: string;
  id: string;
  icon: string;
  name: string;
  teaser: string;
  numberOfReviews: number;
  reviewStars: number;
  pricing: Pricing;
  developerName: string;
  openConsentIn: string;
  isRequiredWixPremium: boolean;
  Tags: WSTag[];
  appDependencies?: IAppDependenciesProps;
  biData: {
    category?: string;
    devNameClickData: Partial<{
      app_id: GUID;
      esi: GUID;
      market: string;
      msid: GUID;
      origin: string;
      site_id: GUID;
      vertical: string;
    }>;
  };
  showRegularAppPage?: boolean;
  isNotAvailableToInstall: boolean;
}

export const SolutionInfoDetails = composer()
  .withTranslation()
  .withMarketplaceStore()
  .withExperiments()
  .compose((props: IDetailsProps) => {
    const isAppInstalled = !!(
      props.marketplaceStore.installedApps &&
      props.marketplaceStore.installedApps.findIndex(
        (installedApp) =>
          installedApp.appId === props.id &&
          installedApp.installationDetails.incompleteSetupReason !==
            IncompleteSetupReason.PENDING_REQUIRED_INSTALLATIONS,
      ) > -1
    );

    return (
      <div
        data-testid="solution-info-details"
        className={s.infoDetailsContainer}
      >
        <Heading
          className={s.infoTitle}
          appearance="H1"
          dataHook="solution-name"
          property="name"
        >
          {props.name}
        </Heading>
        <link
          property="applicationCategory"
          href="https://schema.org/BrowserApplication"
        />

        <Text dataHook="solution-developer" size="small">
          {props.t('solution.page.developerName.by')}
          <DevelopedByWrapper name={props.developerName}>
            <Text
              size="small"
              dataHook="developer-name-solution-description"
              property="brand"
            >
              {config.origin === Origin.STANDALONE && (
                <AppMarketLink
                  to={`${Path.DEVELOPER}/${props.companySlug}`}
                  onClick={() => {
                    config.goto({
                      path: Path.DEVELOPER,
                      slug: props.companySlug,
                    });
                  }}
                >
                  {props.developerName}
                </AppMarketLink>
              )}
              {config.origin !== Origin.STANDALONE && (
                <span>{props.developerName}</span>
              )}
            </Text>
          </DevelopedByWrapper>
        </Text>
        <div className={s.infoDetailsTeaser}>
          <TeaserPreviewWrapper teaser={props.teaser}>
            <Text weight="thin" size="medium" dataHook="solution-info-teaser">
              {props.teaser}
            </Text>
          </TeaserPreviewWrapper>
        </div>
        <RateAndReviewers {...props} />
        <div className={s.btnAndPricing}>
          <HeaderContext.Consumer>
            {(value) => (
              <VisibilityDetector
                onVisible={value.hideHeader}
                onHidden={value.showHeader}
              >
                <AddToSite
                  shouldShowTooltip={props.isNotAvailableToInstall}
                  addToSiteData={props}
                  size="large"
                  location="main_cta"
                  appDependencies={props.appDependencies}
                />
              </VisibilityDetector>
            )}
          </HeaderContext.Consumer>

          <div className={s.planPricing}>
            <SolutionBadge
              t={(k, o) => props.t(k, o)}
              isInstalled={isAppInstalled}
              showPremiumLabel={
                props.pricing &&
                props.pricing.isRequiredWixPremium &&
                !props.marketplaceStore.siteIsPremium
              }
              isBundleAppNotInstalled={isBundleAppAndNotInstalled(
                props.id,
                props.marketplaceStore.bundleApps,
              )}
              isBundleAppInstalled={isBundleAppAndInstalled(
                props.id,
                props.marketplaceStore.bundleApps,
              )}
              isAppSetupIncomplete={isAppPendingConfiguration(
                props.marketplaceStore.installedApps?.find(
                  (installedApp: ManagedApp) => installedApp.appId === props.id,
                ),
              )}
              shouldShowIncompleteSetupPopover={true}
              savedPrice={getBundleAppSavedPrice(
                props.id,
                props.marketplaceStore.bundleApps,
              )}
              pricing={props.pricing}
              companyId={props.companyId}
              isPreInstalled={props.showRegularAppPage}
            />
          </div>
        </div>
      </div>
    );
  });

export const RateAndReviewers = composer()
  .withTranslation()
  .compose((props: IDetailsProps) => {
    const goToReviewsSection = (e) => {
      e.preventDefault();
      const reviewsSection = document.getElementById('reviews');
      reviewsSection && reviewsSection.scrollIntoView();
    };
    const noReviews = props.reviewStars === 0;
    return (
      <div className={s.userRankingContainer}>
        <StarFilled className={noReviews ? s.rateStarGray : s.rateStar} />
        <div className={s.oneStarRating}>
          <Text
            weight={noReviews ? 'thin' : 'bold'}
            dataHook="solution-review-test"
            className={s.reviewStar}
          >
            {noReviews
              ? props.t('solution.page.details.review.empty')
              : Number(props.reviewStars).toFixed(1)}
          </Text>
          {!noReviews && (
            <Text dataHook="solution-review-test">
              <a href="#reviews" onClick={goToReviewsSection}>
                {' '}
                (<span>{props.numberOfReviews}</span>)
              </a>
            </Text>
          )}
        </div>
      </div>
    );
  });

export const dtoBaseToDeatilsBiData = (baseInfo) => {
  return {
    category: categoryFilter(baseInfo.Tags),
    devNameClickData: {
      app_id: baseInfo.id,
      market: config.originForBI,
      msid: '',
      origin: config.origin,
      vertical: 'solution-page',
    },
  };
};

export const categoryFilter = (tags: WSTag[]): string => {
  const filteredTags =
    tags && tags.filter((tag) => !tag.parentSlug && tag.type === 'CATEGORY');
  const categoryName =
    (filteredTags && filteredTags.length > 0 && filteredTags[0].name) || '';
  return categoryName;
};
